import React, { memo, ReactNode, useMemo } from 'react';

import * as C from './Charts.styled';

const DASH = '—';

export interface IFunnelStep {
  id: string;
  label: string;
  /** `null` — шага ещё не знаем (поле не пришло): «—», столбика нет. */
  value: number | null;
  /** Значение сравнения — бледный столбик под текущим. */
  compare?: number | null;
  valueText: string;
  /** Строка под шагом: «отправлено, не подписано: 7». */
  note?: ReactNode;
}

export interface IFunnelRow extends IFunnelStep {
  /** Ширина столбика, % от самого большого шага (текущего и сравнения). */
  width: number;
  compareWidth: number | null;
  /** Доля от предыдущего известного шага 0..1; `null` — не считается. */
  fromPrevious: number | null;
  /**
   * Шаг больше предыдущего (переход > 100 %: договоры отправлены лидам,
   * пришедшим до начала истории или до окна) — доля не показывается, «—».
   */
  overflow: boolean;
}

/**
 * Шаги воронки: ширины — от общего максимума, переход — от ПРЕДЫДУЩЕГО шага
 * (а не от первого). Неизвестный шаг рвёт цепочку: доля следующего от него
 * не считается — «—» честнее, чем процент от прочерка. Переход больше
 * 100 % — тоже «—» (`overflow`): «110 % лидов получили договор» — не
 * переход, а разные люди в окне (повторное ревью 0103, A13).
 */
export const funnelRows = (steps: ReadonlyArray<IFunnelStep>): IFunnelRow[] => {
  const max = Math.max(
    0,
    ...steps.flatMap((step) => [step.value ?? 0, step.compare ?? 0]),
  );
  const pct = (value: number | null | undefined) =>
    value === null || value === undefined || max <= 0
      ? 0
      : Math.min(100, (value / max) * 100);
  return steps.map((step, index) => {
    const before = index > 0 ? steps[index - 1].value : null;
    const share =
      index > 0 && before !== null && before > 0 && step.value !== null
        ? step.value / before
        : null;
    const overflow = share !== null && share > 1;
    return {
      ...step,
      width: pct(step.value),
      compareWidth:
        step.compare === null || step.compare === undefined
          ? null
          : pct(step.compare),
      fromPrevious: overflow ? null : share,
      overflow,
    };
  });
};

/** Есть переход больше 100 % — экрану нужна подпись почему «—». */
export const funnelHasOverflow = (steps: ReadonlyArray<IFunnelStep>): boolean =>
  funnelRows(steps).some((row) => row.overflow);

export interface IFunnelBarsProps {
  steps: ReadonlyArray<IFunnelStep>;
  ariaLabel: string;
  /** «48 %» — подпись перехода от предыдущего шага. */
  formatShare: (share: number) => string;
  color?: string;
  /** Клик по шагу — уводит туда, где этот шаг разбирается. */
  onSelect?: (id: string) => void;
  /** Подсказка к «—» перехода больше 100 %. */
  overflowTitle?: string;
}

/**
 * ТЗ 0103 — воронка периода в HTML: шаг, столбик, доля перехода от
 * предыдущего шага и бледный столбик сравнения.
 */
export const FunnelBars = memo(
  ({
    steps,
    ariaLabel,
    formatShare,
    color,
    onSelect,
    overflowTitle,
  }: IFunnelBarsProps) => {
    const rows = useMemo(() => funnelRows(steps), [steps]);
    return (
      <C.FunnelList aria-label={ariaLabel}>
        {rows.map((row) => {
          const body = (
            <>
              <C.RankHead>
                <C.RankLabel title={row.label}>{row.label}</C.RankLabel>
                <C.RankValue $muted={row.value === null}>
                  {row.valueText}
                  {row.fromPrevious !== null && (
                    <C.FunnelShare>
                      {formatShare(row.fromPrevious)}
                    </C.FunnelShare>
                  )}
                  {row.overflow && (
                    <C.FunnelShare title={overflowTitle}>{DASH}</C.FunnelShare>
                  )}
                </C.RankValue>
              </C.RankHead>
              <C.FunnelTrack aria-hidden="true">
                {row.compareWidth !== null && (
                  <C.FunnelCompare $width={row.compareWidth} $color={color} />
                )}
                <C.FunnelFill $width={row.width} $color={color} />
              </C.FunnelTrack>
              {row.note && <C.RankSecondary>{row.note}</C.RankSecondary>}
            </>
          );
          return (
            <C.RankItem key={row.id}>
              {onSelect ? (
                <C.RankButton type="button" onClick={() => onSelect(row.id)}>
                  {body}
                </C.RankButton>
              ) : (
                <C.RankStatic>{body}</C.RankStatic>
              )}
            </C.RankItem>
          );
        })}
      </C.FunnelList>
    );
  },
);

FunnelBars.displayName = 'FunnelBars';
